import { Folder, FolderPlus, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import type { MaterialFolder } from '@/types/material'

interface MaterialFolderTreeProps {
	folders: MaterialFolder[]
	selectedId: number
	collapsed?: boolean
	onSelect: (id: number, name: string) => void
	onCreate: (parent?: MaterialFolder) => void
	onEdit: (folder: MaterialFolder) => void
	onDelete: (folder: MaterialFolder) => void
}

export function MaterialFolderTree({
	folders,
	selectedId,
	collapsed = false,
	onSelect,
	onCreate,
	onEdit,
	onDelete,
}: MaterialFolderTreeProps) {
	const renderFolder = (folder: MaterialFolder) => (
		<div key={folder.id} className="space-y-1">
			<div className={`flex items-center gap-1 rounded-md px-2 py-1.5 ${selectedId === folder.id ? 'bg-muted' : ''}`}>
				<button
					type="button"
					className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
					onClick={() => onSelect(folder.id, folder.name)}
				>
					<Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
					<span className="truncate">{folder.name}</span>
				</button>
				<PermissionGuard permission="material:folder:create">
					<Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => onCreate(folder)}>
						<FolderPlus className="h-4 w-4" />
						<span className="sr-only">新增子目录</span>
					</Button>
				</PermissionGuard>
				<PermissionGuard permission="material:folder:update">
					<Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => onEdit(folder)}>
						<Pencil className="h-4 w-4" />
						<span className="sr-only">重命名目录</span>
					</Button>
				</PermissionGuard>
				<PermissionGuard permission="material:folder:delete">
					<Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => onDelete(folder)}>
						<Trash2 className="h-4 w-4" />
						<span className="sr-only">删除目录</span>
					</Button>
				</PermissionGuard>
			</div>
			{!collapsed && folder.children?.length ? (
				<div className="ml-4 border-l pl-2">{folder.children.map(renderFolder)}</div>
			) : null}
		</div>
	)

	return (
		<div className="space-y-2">
			<div className={`flex items-center gap-1 rounded-md px-2 py-1.5 ${selectedId === 0 ? 'bg-muted' : ''}`}>
				<button
					type="button"
					className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
					onClick={() => onSelect(0, '根目录')}
				>
					<Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
					<span className="truncate">根目录</span>
				</button>
				<PermissionGuard permission="material:folder:create">
					<Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => onCreate()}>
						<FolderPlus className="h-4 w-4" />
						<span className="sr-only">新增目录</span>
					</Button>
				</PermissionGuard>
			</div>
			{folders.map(renderFolder)}
		</div>
	)
}
